import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function LifeInsuranceMistakesIndianaFamilies() {
  useEffect(() => {
    const SITE_URL = 'https://hoosierlifeinsurance.com';
    document.title = '7 Life Insurance Mistakes Indiana Families Make';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Avoid the 7 most costly life insurance mistakes Indiana families make. Real examples, real consequences, and exactly what to do instead.');

    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', 'life insurance mistakes Indiana, life insurance tips Indiana families, avoid life insurance errors');

    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_URL}/resources/life-insurance-mistakes-indiana-families`;

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: '7 Life Insurance Mistakes Indiana Families Make (And How to Avoid Them)',
        description: 'Don\'t make these costly life insurance mistakes. Learn the 7 most common errors Indiana families make when buying coverage—and how to protect your loved ones the right way.',
        author: {
          '@type': 'Person',
          name: 'Benjamin Wright'
        },
        publisher: {
          '@type': 'Organization',
          name: 'Hoosier Life Insurance',
          url: SITE_URL
        },
        datePublished: '2025-01-12T00:00:00Z',
        dateModified: new Date().toISOString(),
        url: `${SITE_URL}/resources/life-insurance-mistakes-indiana-families`,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/resources/life-insurance-mistakes-indiana-families`
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: SITE_URL
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Resources',
            item: `${SITE_URL}/resources`
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: '7 Life Insurance Mistakes Indiana Families Make',
            item: `${SITE_URL}/resources/life-insurance-mistakes-indiana-families`
          }
        ]
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: '7 Life Insurance Mistakes Indiana Families Make (And How to Avoid Them)',
        description: 'Don\'t make these costly life insurance mistakes. Learn the 7 most common errors Indiana families make when buying coverage—and how to protect your loved ones the right way.',
        url: `${SITE_URL}/resources/life-insurance-mistakes-indiana-families`,
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          url: SITE_URL,
          name: 'Hoosier Life Insurance'
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What is the biggest life insurance mistake Indiana families make?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'The biggest mistake is relying only on employer-provided life insurance. Most Indiana employers offer just 1-2 times your annual salary in group coverage—far below what most families need—and that coverage disappears completely if you change jobs, get laid off, or retire. You should always supplement employer coverage with a personal term life policy that you own and control.'
            }
          },
          {
            '@type': 'Question',
            name: 'Does a stay-at-home parent in Indiana need life insurance?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Absolutely. A stay-at-home parent in Indiana provides services worth $40,000-$60,000 per year including childcare ($12,000-$18,000/year per child), housekeeping ($8,000-$12,000/year), meal preparation, and transportation. If the stay-at-home parent dies, the surviving working parent must either quit their job or pay for all these services. Most Indiana stay-at-home parents should have $250,000-$500,000 in coverage.'
            }
          },
          {
            '@type': 'Question',
            name: 'Should Indiana homeowners buy mortgage life insurance from their lender?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'No. Lender-sold mortgage life insurance is 2-3 times more expensive than a personal term life policy, and the death benefit goes to the bank—not your family. The coverage also decreases as you pay down the mortgage while your premium stays the same. Instead, buy your own term life policy: for a $300,000 mortgage in Indianapolis, a personal 30-year term policy costs about $45/month versus $95/month for lender mortgage insurance, and your family receives the full death benefit directly.'
            }
          },
          {
            '@type': 'Question',
            name: 'Is whole life insurance better than term life for most Indiana families?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'For 90% of Indiana families, term life insurance is the better choice. Whole life costs 8-10 times more than term for the same death benefit. A 35-year-old can get $500,000 of 30-year term life for $35/month versus $420/month for whole life. Most Indiana families are better served by buying affordable term life coverage and investing the difference in a 401(k) or Roth IRA. Whole life makes sense for high-net-worth families doing estate planning or business owners with specific succession needs.'
            }
          },
          {
            '@type': 'Question',
            name: 'How often should Indiana families review their life insurance coverage?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Review your life insurance every 2-3 years or after any major life event. Events that require a coverage review include: marriage or divorce, birth or adoption of a child, purchasing a home, a significant income increase, a job change (especially if you lose employer coverage), a new health diagnosis, or your children becoming financially independent. At each review, check that your coverage amount is still adequate, your beneficiaries are up to date, and you are not overpaying for your policy.'
            }
          }
        ]
      }
    ];

    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `schema-resource-mistakes-${index}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      schemas.forEach((_, index) => {
        const script = document.getElementById(`schema-resource-mistakes-${index}`);
        if (script) {
          document.head.removeChild(script);
        }
      });
    };
  }, []);

  return (
    <>
      <title>7 Life Insurance Mistakes Indiana Families Make | Hoosier Life Insurance</title>
      <article className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white py-20">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative max-w-4xl mx-auto px-6">
            <div className="mb-6">
              <Link to="/resources" className="inline-flex items-center text-white/70 hover:text-white transition-colors text-sm">
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Resources
              </Link>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">7 Life Insurance Mistakes Indiana Families Make (And How to Avoid Them)</h1>
            <p className="text-xl text-white/80 mb-8">Don't let these common errors leave your family unprotected. Learn what to avoid when buying life insurance in Indiana.</p>
            <div className="flex flex-wrap gap-4 text-sm text-white/70">
              <span><i className="ri-calendar-line mr-2"></i>Updated January 2026</span>
              <span><i className="ri-time-line mr-2"></i>10 min read</span>
              <span><i className="ri-map-pin-line mr-2"></i>Indiana Family Guide</span>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Life insurance is one of the most important financial decisions you'll make for your Indiana family—yet it's also one of the easiest to get wrong. A single mistake can leave your loved ones underinsured, overpaying, or completely unprotected.
            </p>
            <p className="text-gray-700 leading-relaxed">
              After helping thousands of Indiana families secure coverage, we've seen the same mistakes repeated over and over. The good news? They're all preventable. Here are the 7 most common life insurance mistakes Indiana families make—and exactly how to avoid them.
            </p>
          </div>

          {/* Mistake 1 */}
          <div className="mb-16">
            <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-8 mb-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-red-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-2xl font-bold">
                  1
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">Relying Only on Employer-Provided Life Insurance</h2>
                  <p className="text-xl text-gray-700">
                    <strong>The Mistake:</strong> Assuming your employer's group life insurance is enough to protect your family.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Why This Is Dangerous</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1 text-xl"></i>
                  <span><strong>Insufficient coverage:</strong> Most Indiana employers offer 1-2x your salary. If you earn $75,000, that's only $75,000-$150,000—not enough to replace your income, pay off the mortgage, and fund college.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1 text-xl"></i>
                  <span><strong>Coverage ends when you leave:</strong> Change jobs, get laid off, or retire? Your coverage disappears. And you'll be older and more expensive to insure when you try to replace it.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1 text-xl"></i>
                  <span><strong>No portability:</strong> You can't take employer coverage with you. If you develop health issues while employed, you may become uninsurable after leaving.</span>
                </li>
              </ul>
            </div>

            <div className="bg-[#FDF6F0] rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-lightbulb-line text-[#D97642] mr-3 text-2xl"></i>
                The Fix
              </h3>
              <p className="text-gray-700 mb-4">
                <strong>Keep your employer coverage as a bonus, but purchase your own personal term life policy.</strong> A 30-year-old Indiana resident can get $500,000 in 30-year term coverage for $30-$40/month—less than a Netflix subscription.
              </p>
              <p className="text-gray-700 text-sm">
                <strong>Example:</strong> Employer provides $100,000. You buy an additional $500,000 personal policy. Total coverage: $600,000. If you leave your job, you still have $500,000 protection.
              </p>
            </div>
          </div>

          {/* Mistake 2 */}
          <div className="mb-16">
            <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-8 mb-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-red-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-2xl font-bold">
                  2
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">Not Insuring the Stay-at-Home Parent</h2>
                  <p className="text-xl text-gray-700">
                    <strong>The Mistake:</strong> Only buying life insurance for the working spouse because "the stay-at-home parent doesn't earn income."
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Why This Is Dangerous</h3>
              <p className="text-gray-700 mb-4">
                A stay-at-home parent in Indiana provides services worth <strong>$40,000-$60,000 per year</strong>:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-bold text-gray-900 mb-2">Childcare</p>
                  <p className="text-sm text-gray-700">$12,000-$18,000/year per child in Indiana</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-bold text-gray-900 mb-2">Housekeeping</p>
                  <p className="text-sm text-gray-700">$8,000-$12,000/year</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-bold text-gray-900 mb-2">Meal Preparation</p>
                  <p className="text-sm text-gray-700">$6,000-$10,000/year</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-bold text-gray-900 mb-2">Transportation</p>
                  <p className="text-sm text-gray-700">$4,000-$6,000/year</p>
                </div>
              </div>
              <p className="text-gray-700 mt-4">
                If the stay-at-home parent dies, the working parent must either quit their job or pay for all these services—while grieving and caring for children.
              </p>
            </div>

            <div className="bg-[#FDF6F0] rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-lightbulb-line text-[#D97642] mr-3 text-2xl"></i>
                The Fix
              </h3>
              <p className="text-gray-700 mb-4">
                <strong>Insure the stay-at-home parent for $250,000-$500,000.</strong> This covers 5-10 years of childcare, housekeeping, and other services while the surviving parent adjusts.
              </p>
              <p className="text-gray-700 text-sm">
                <strong>Cost:</strong> A 35-year-old stay-at-home mom in Indiana can get $300,000 in 20-year term coverage for $20-$25/month.
              </p>
            </div>
          </div>

          {/* Mistake 3 */}
          <div className="mb-16">
            <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-8 mb-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-red-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-2xl font-bold">
                  3
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">Buying Mortgage Life Insurance from Your Lender</h2>
                  <p className="text-xl text-gray-700">
                    <strong>The Mistake:</strong> Purchasing mortgage life insurance offered by your bank or mortgage company.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Why This Is Dangerous</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1 text-xl"></i>
                  <span><strong>2-3x more expensive:</strong> Lender-sold mortgage insurance costs $80-$120/month for $300,000 coverage. A personal term life policy costs $30-$40/month for the same amount.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1 text-xl"></i>
                  <span><strong>Death benefit goes to the bank, not your family:</strong> If you die, the insurance pays off the mortgage—but your family gets nothing. With a personal policy, your family receives the full death benefit and can choose how to use it.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1 text-xl"></i>
                  <span><strong>Decreasing coverage, fixed premiums:</strong> As you pay down your mortgage, the death benefit decreases—but your premium stays the same. You're paying more for less coverage over time.</span>
                </li>
              </ul>
            </div>

            <div className="bg-[#FDF6F0] rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-lightbulb-line text-[#D97642] mr-3 text-2xl"></i>
                The Fix
              </h3>
              <p className="text-gray-700 mb-4">
                <strong>Buy your own term life policy instead.</strong> It's cheaper, the death benefit goes to your family (not the bank), and the coverage amount stays level.
              </p>
              <div className="bg-white rounded-lg p-4 mt-4">
                <p className="font-bold text-gray-900 mb-2">Comparison Example (Indianapolis homeowner, $300,000 mortgage)</p>
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="flex justify-between pb-2 border-b border-gray-200">
                    <span>Lender mortgage insurance</span>
                    <span className="font-bold text-red-600">$95/month</span>
                  </div>
                  <div className="flex justify-between pb-2 border-b border-gray-200">
                    <span>Personal 30-year term life ($500,000)</span>
                    <span className="font-bold text-teal-600">$45/month</span>
                  </div>
                  <div className="flex justify-between pt-2">
                    <span className="font-bold">You save + get $200K more coverage</span>
                    <span className="font-bold text-green-600">$50/month</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mistake 4 */}
          <div className="mb-16">
            <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-8 mb-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-red-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-2xl font-bold">
                  4
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">Underinsuring to Keep Premiums Low</h2>
                  <p className="text-xl text-gray-700">
                    <strong>The Mistake:</strong> Buying $100,000-$250,000 in coverage because "that's all we can afford" when your family actually needs $500,000-$1 million.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Why This Is Dangerous</h3>
              <p className="text-gray-700 mb-4">
                <strong>$100,000 sounds like a lot—until you do the math:</strong>
              </p>
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="space-y-3 text-gray-700">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span>Funeral and final expenses</span>
                    <span className="font-bold">-$12,000</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span>Outstanding medical bills</span>
                    <span className="font-bold">-$8,000</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span>Credit card debt</span>
                    <span className="font-bold">-$10,000</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span>Car loans</span>
                    <span className="font-bold">-$20,000</span>
                  </div>
                  <div className="flex justify-between items-center pt-3 text-lg">
                    <span className="font-bold">Remaining for mortgage + living expenses</span>
                    <span className="font-bold text-red-600">$50,000</span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mt-4">$50,000 covers less than 2 years of expenses for most Indiana families. Then what?</p>
              </div>
            </div>

            <div className="bg-[#FDF6F0] rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-lightbulb-line text-[#D97642] mr-3 text-2xl"></i>
                The Fix
              </h3>
              <p className="text-gray-700 mb-4">
                <strong>Use the DIME method to calculate your actual needs:</strong> Debt + Income replacement + Mortgage + Education costs. Most Indiana families need 10-15x their annual income.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Good news:</strong> Term life insurance is incredibly affordable. The difference between $250,000 and $1 million coverage is often just $30-$40/month—less than a family dinner out.
              </p>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-700"><strong>Example:</strong> Age 35, Indiana resident, 30-year term</p>
                <ul className="space-y-1 text-sm text-gray-700 mt-2">
                  <li>• $250,000 coverage = $25/month</li>
                  <li>• $500,000 coverage = $35/month</li>
                  <li>• $1,000,000 coverage = $60/month</li>
                </ul>
                <p className="text-sm text-teal-600 font-bold mt-3">For $35/month more, you get 4x the protection. Worth it?</p>
              </div>
            </div>
          </div>

          {/* Mistake 5 */}
          <div className="mb-16">
            <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-8 mb-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-red-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-2xl font-bold">
                  5
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">Waiting Until You "Need" It</h2>
                  <p className="text-xl text-gray-700">
                    <strong>The Mistake:</strong> Putting off life insurance until you're older, have health issues, or "get around to it."
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Why This Is Dangerous</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1 text-xl"></i>
                  <span><strong>Premiums increase 8-10% per year of age:</strong> A 30-year-old pays $30/month for $500K coverage. A 40-year-old pays $50/month. A 50-year-old pays $120/month. Same coverage, 4x the cost.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1 text-xl"></i>
                  <span><strong>Health issues make you uninsurable:</strong> Develop diabetes, high blood pressure, or heart disease? You'll pay 2-3x more—or be declined entirely.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1 text-xl"></i>
                  <span><strong>Tragedy doesn't wait:</strong> 40% of Americans will die or become disabled before age 65. "I'll get it later" is a gamble you can't afford to lose.</span>
                </li>
              </ul>
            </div>

            <div className="bg-[#FDF6F0] rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-lightbulb-line text-[#D97642] mr-3 text-2xl"></i>
                The Fix
              </h3>
              <p className="text-gray-700 mb-4">
                <strong>Buy life insurance NOW, while you're young and healthy.</strong> Every year you wait costs you thousands of dollars over the life of the policy.
              </p>
              <div className="bg-white rounded-lg p-4">
                <p className="font-bold text-gray-900 mb-3">Cost of Waiting: $500,000 Coverage, 30-Year Term</p>
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="flex justify-between pb-2 border-b border-gray-200">
                    <span>Buy at age 30</span>
                    <span className="font-bold">$30/month × 360 months = <span className="text-teal-600">$10,800 total</span></span>
                  </div>
                  <div className="flex justify-between pb-2 border-b border-gray-200">
                    <span>Buy at age 40</span>
                    <span className="font-bold">$50/month × 360 months = <span className="text-red-600">$18,000 total</span></span>
                  </div>
                  <div className="flex justify-between pt-2">
                    <span className="font-bold">Cost of waiting 10 years</span>
                    <span className="font-bold text-red-600">$7,200 more</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mistake 6 */}
          <div className="mb-16">
            <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-8 mb-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-red-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-2xl font-bold">
                  6
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">Choosing Whole Life When You Need Term Life</h2>
                  <p className="text-xl text-gray-700">
                    <strong>The Mistake:</strong> Buying expensive whole life insurance because an agent said "it's an investment" when term life would better serve your needs.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Why This Is Dangerous</h3>
              <p className="text-gray-700 mb-4">
                <strong>Whole life costs 8-10x more than term life for the same death benefit:</strong>
              </p>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-teal-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">Term Life (30-Year)</h4>
                  <p className="text-gray-700 text-sm mb-2">Age 35, $500,000 coverage</p>
                  <p className="text-3xl font-bold text-teal-600 mb-2">$35/month</p>
                  <p className="text-sm text-gray-600">Covers your mortgage term, kids through college, income replacement</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">Whole Life (Permanent)</h4>
                  <p className="text-gray-700 text-sm mb-2">Age 35, $500,000 coverage</p>
                  <p className="text-3xl font-bold text-gray-600 mb-2">$420/month</p>
                  <p className="text-sm text-gray-600">Permanent coverage + cash value, but $385/month more expensive</p>
                </div>
              </div>
              <p className="text-gray-700">
                <strong>The problem:</strong> Most Indiana families can't afford $420/month, so they buy $100,000 whole life instead—leaving them dangerously underinsured.
              </p>
            </div>

            <div className="bg-[#FDF6F0] rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-lightbulb-line text-[#D97642] mr-3 text-2xl"></i>
                The Fix
              </h3>
              <p className="text-gray-700 mb-4">
                <strong>For 90% of Indiana families, term life is the right choice.</strong> It's affordable, provides massive coverage during your working years, and frees up money for retirement savings.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>When whole life makes sense:</strong> High-net-worth families ($1M+ net worth), business owners needing estate planning, or those who've maxed out all other retirement accounts.
              </p>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-700"><strong>Better strategy:</strong> Buy $500,000 term life for $35/month. Invest the $385/month you save in a Roth IRA earning 8% annually. After 30 years, you'll have $568,000—far more than whole life cash value.</p>
              </div>
            </div>
          </div>

          {/* Mistake 7 */}
          <div className="mb-16">
            <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-8 mb-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-red-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-2xl font-bold">
                  7
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">Never Reviewing or Updating Your Policy</h2>
                  <p className="text-xl text-gray-700">
                    <strong>The Mistake:</strong> Buying life insurance once and never looking at it again—even after major life changes.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Why This Is Dangerous</h3>
              <p className="text-gray-700 mb-4">
                <strong>Your life changes—your coverage should too.</strong> Events that require a policy review:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-bold text-gray-900 mb-2 flex items-center">
                    <i className="ri-heart-line text-red-500 mr-2"></i>
                    Marriage
                  </p>
                  <p className="text-sm text-gray-700">Your spouse now depends on your income. Increase coverage.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-bold text-gray-900 mb-2 flex items-center">
                    <i className="ri-baby-line text-teal-600 mr-2"></i>
                    New Baby
                  </p>
                  <p className="text-sm text-gray-700">Add $100,000-$200,000 per child for college and expenses.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-bold text-gray-900 mb-2 flex items-center">
                    <i className="ri-home-4-line text-teal-600 mr-2"></i>
                    Home Purchase
                  </p>
                  <p className="text-sm text-gray-700">Increase coverage to match your new mortgage balance.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-bold text-gray-900 mb-2 flex items-center">
                    <i className="ri-arrow-up-line text-green-600 mr-2"></i>
                    Income Increase
                  </p>
                  <p className="text-sm text-gray-700">Earning more? Your family needs more income replacement.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-bold text-gray-900 mb-2 flex items-center">
                    <i className="ri-briefcase-line text-gray-600 mr-2"></i>
                    Job Change
                  </p>
                  <p className="text-sm text-gray-700">Lost employer coverage? Replace it immediately.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-bold text-gray-900 mb-2 flex items-center">
                    <i className="ri-heart-add-line text-red-500 mr-2"></i>
                    Health Diagnosis
                  </p>
                  <p className="text-sm text-gray-700">Lock in coverage now before conditions worsen.</p>
                </div>
              </div>
            </div>

            <div className="bg-[#FDF6F0] rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-lightbulb-line text-[#D97642] mr-3 text-2xl"></i>
                The Fix
              </h3>
              <p className="text-gray-700 mb-4">
                <strong>Review your life insurance every 2-3 years or after any major life event.</strong> Set a calendar reminder to check:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-3 mt-1"></i>
                  <span>Is your coverage amount still adequate?</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-3 mt-1"></i>
                  <span>Are your beneficiaries up to date?</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-3 mt-1"></i>
                  <span>Do you need to add coverage for new dependents?</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-3 mt-1"></i>
                  <span>Can you get better rates by shopping around?</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Quick Checklist */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Your Life Insurance Checklist: Are You Making These Mistakes?</h2>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8">
              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-white rounded-lg p-4">
                  <input type="checkbox" className="w-6 h-6 mt-1 text-teal-600 rounded" />
                  <label className="text-gray-700">I have my own personal life insurance policy (not just employer coverage)</label>
                </div>
                <div className="flex items-start gap-4 bg-white rounded-lg p-4">
                  <input type="checkbox" className="w-6 h-6 mt-1 text-teal-600 rounded" />
                  <label className="text-gray-700">Both spouses are insured (including stay-at-home parents)</label>
                </div>
                <div className="flex items-start gap-4 bg-white rounded-lg p-4">
                  <input type="checkbox" className="w-6 h-6 mt-1 text-teal-600 rounded" />
                  <label className="text-gray-700">I have 10-15x my annual income in coverage</label>
                </div>
                <div className="flex items-start gap-4 bg-white rounded-lg p-4">
                  <input type="checkbox" className="w-6 h-6 mt-1 text-teal-600 rounded" />
                  <label className="text-gray-700">I bought my policy while young and healthy (not waiting)</label>
                </div>
                <div className="flex items-start gap-4 bg-white rounded-lg p-4">
                  <input type="checkbox" className="w-6 h-6 mt-1 text-teal-600 rounded" />
                  <label className="text-gray-700">I chose term life (unless I'm high-net-worth and need whole life)</label>
                </div>
                <div className="flex items-start gap-4 bg-white rounded-lg p-4">
                  <input type="checkbox" className="w-6 h-6 mt-1 text-teal-600 rounded" />
                  <label className="text-gray-700">I avoided lender-sold mortgage insurance</label>
                </div>
                <div className="flex items-start gap-4 bg-white rounded-lg p-4">
                  <input type="checkbox" className="w-6 h-6 mt-1 text-teal-600 rounded" />
                  <label className="text-gray-700">I review my policy every 2-3 years or after major life events</label>
                </div>
              </div>
              <div className="mt-6 bg-teal-600 text-white rounded-xl p-6">
                <p className="text-lg font-bold mb-2">How many did you check?</p>
                <ul className="space-y-2 text-teal-50 text-sm">
                  <li><strong>7/7:</strong> You're doing everything right! Keep it up.</li>
                  <li><strong>4-6:</strong> You're on the right track but have room for improvement.</li>
                  <li><strong>0-3:</strong> Your family may be at risk. Time to fix these mistakes.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white rounded-2xl p-8 mb-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Fix These Mistakes and Protect Your Indiana Family?</h2>
            <p className="text-xl text-white/80 mb-8">
              Let's review your current coverage (or help you get started) and make sure you're not making any of these costly errors.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <Link 
                to="/instant-quote" 
                className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Get Free Quote
              </Link>
              <Link 
                to="/book-appointment" 
                className="bg-[#0F2A3F] text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-[#0A1F2E] transition-colors border-2 border-white/30 whitespace-nowrap"
              >
                Schedule a Review
              </Link>
            </div>
          </div>

          {/* Related Articles */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Resources</h2>

            {/* Product Pages — two-way authority signal */}
            <div className="mb-4">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Product Pages</p>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <Link to="/term-life" className="group bg-white border-2 border-[#D97642] rounded-xl p-5 hover:bg-[#FDF6F0] transition-colors">
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className="ri-calendar-check-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#D97642] transition-colors">Term Life Insurance</h3>
                  <p className="text-xs text-gray-500">The right fix for 90% of Indiana families — from $25/mo</p>
                </Link>
                <Link to="/whole-life" className="group bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-[#D97642] hover:bg-[#FDF6F0] transition-colors">
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className="ri-infinity-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#D97642] transition-colors">Whole Life Insurance</h3>
                  <p className="text-xs text-gray-500">When permanent coverage makes sense for your family</p>
                </Link>
                <Link to="/final-expense" className="group bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-[#D97642] hover:bg-[#FDF6F0] transition-colors">
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className="ri-hand-heart-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#D97642] transition-colors">Final Expense Insurance</h3>
                  <p className="text-xs text-gray-500">Guaranteed acceptance burial insurance — no medical exam</p>
                </Link>
              </div>
            </div>

            {/* Articles */}
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Articles &amp; Guides</p>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/resources/how-much-life-insurance-indiana-families-need" className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className="ri-calculator-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">How Much Coverage Do You Need?</h3>
                <p className="text-sm text-gray-600">Calculate the right amount using the DIME method</p>
              </Link>

              <Link to="/resources/term-vs-whole-life-indianapolis-homeowners" className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className="ri-scales-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">Term vs. Whole Life for Indianapolis Homeowners</h3>
                <p className="text-sm text-gray-600">Which policy type is right for you?</p>
              </Link>

              <Link to="/blog/life-insurance-rates-indianapolis" className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className="ri-price-tag-3-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">Life Insurance Rates in Indianapolis</h3>
                <p className="text-sm text-gray-600">Current rates by age, health class, and coverage amount</p>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Questions About Your Life Insurance Coverage?</h2>
            <p className="text-xl text-gray-700 mb-8">
              Our Indiana-based agents help families avoid these mistakes every day. Let's make sure your family is properly protected.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:3176482983" className="bg-[#1A3A52] text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-[#0F2A3F] transition-colors whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-phone-line mr-2"></i>
                Call (317) 648-2983
              </a>
              <Link to="/contact" className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-100 transition-colors border-2 border-[#1A3A52] whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-message-3-line mr-2"></i>
                Send a Message
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}